import { useEffect, useState } from "react";

export interface CheckedBoxes {
  [uuid: string]: boolean;
}

export function useTableCheckboxes(
  rows: { uuid: string }[],
  defaultSelected?: { uuid: string }[],
  sendSelected?: (selected: { uuid: string }[]) => void
) {
  const rowUuids = rows.map((row) => row.uuid);

  const defaultCheckboxes: { [key: string]: boolean } = {};
  if (defaultSelected) defaultSelected.forEach(({ uuid }) => (defaultCheckboxes[uuid] = true));
  const [checkedBoxes, setCheckedBoxes] = useState<CheckedBoxes>(defaultCheckboxes);

  // When rows changes, remove checkedboxes of uuids not in the rows anymore
  useEffect(() => {
    const newCheckedBoxes = { ...checkedBoxes };

    const removed = !!Object.keys(newCheckedBoxes).find((key) => {
      if (!rowUuids.includes(key)) {
        delete newCheckedBoxes[key];
        return true;
      }
      return;
    });

    if (removed) setCheckedBoxes(newCheckedBoxes);
  }, [rows, checkedBoxes, rowUuids]);

  const uuidChecked = (uuid: string) => !!checkedBoxes[uuid];

  const setChecked = (uuid: string, event: React.ChangeEvent<HTMLInputElement>) =>
    setCheckedBoxes({ ...checkedBoxes, [uuid]: !!(event.target && event.target.checked) });

  const allChecked = rowUuids.every((uuid) => checkedBoxes[uuid]);
  const someChecked = rowUuids.some((uuid) => checkedBoxes[uuid]);

  const setAllChecked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked: CheckedBoxes = {};
    for (const uuid of rowUuids) {
      newChecked[uuid] = !!event.target.checked;
    }
    setCheckedBoxes(newChecked);
  };

  const deselectAll = () => {
    setCheckedBoxes({});
  };

  const checkedRows = rows.filter((row) => uuidChecked(row.uuid));

  useEffect(() => {
    if (sendSelected) sendSelected(checkedRows);
  }, [checkedBoxes]);

  return {
    uuidChecked,
    setChecked,
    allChecked,
    someChecked,
    setAllChecked,
    deselectAll,
    checkedRows,
  };
}
